import Divider from "@material-ui/core/Divider";
import Grid from "@material-ui/core/Grid";
import ListSubheader from "@material-ui/core/ListSubheader";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import HomeIcon from "@material-ui/icons/HomeRounded";
import { useFormik } from "formik";
import GoogleMapReact from "google-map-react";
import React, { useEffect } from "react";
import * as yup from "yup";
import IconButton from "@material-ui/core/IconButton";
import CopyIcon from "@material-ui/icons/FileCopyRounded";
import { useSnackbar } from "notistack";

import ConditionalTooltip from "../../../ConditionalTooltip";
import HelpAdornment from "../../../InputAdornments/Help";
import * as constants from "../../../../constants";
import * as colors from "../../../../styles/colors";
import { formatAddress } from '../../../../utils/address';


const Location = ({ property, admin, disabled, onEdit, onSubmit }) => {
  const formik = useFormik({
    initialValues: {
      place: property.place
    },
    validationSchema: yup.object({
      place: yup.object({
        addressComponents: yup.object({
          streetNumber: yup.string().required('Required'),
          streetName: yup.string().required('Required'),
          subpremise: yup.string().nullable(),
          city: yup.string().nullable('Required'),
          state: yup.string().nullable('Required'),
          zipCode: yup.string().nullable('Required'),
          country: yup.string().nullable('Required')
        }),
        coordinates: yup.object({
          lat: yup.number(),
          lng: yup.number()
        })
      })
    }),
    onSubmit: values => {
      onSubmit(
        {
          propertyId: property.propertyId,
          place: {
            addressComponents: {
              streetNumber: values.place.addressComponents.streetNumber,
              streetName: values.place.addressComponents.streetName,
              subpremise: values.place.addressComponents.subpremise,
              city: values.place.addressComponents.city,
              state: values.place.addressComponents.state,
              zipCode: values.place.addressComponents.zipCode,
              country: values.place.addressComponents.country
            },
            coordinates: {
              lat: values.place.coordinates.lat,
              lng: values.place.coordinates.lng
            },
            formattedAddress: formatAddress(values.place.addressComponents)
          }
        },
        property => {
          formik.resetForm({
            values: {
              place: property.place
            }
          });
        }
      );
    }
  });
  const snackbar = useSnackbar();
  const enqueueSnackbar = snackbar.enqueueSnackbar;
  useEffect(() => {
    if (formik.dirty) {
      const resetForm = property => {
        formik.resetForm({
          values: {
            place: property.place
          }
        });
      };
      onEdit(formik.handleSubmit, resetForm);
    }
  }, [formik.dirty]);
  useEffect(() => {
    if (!formik.dirty) {
      formik.resetForm({
        values: {
          place: property.place
        }
      });
    }
  }, [property]);
  const handleMapClick = ({ lat, lng }) => {
    if (!disabled) {
      formik.setFieldValue("place.coordinates", { lat, lng });
    }
  };
  const handleCopyAddress = () => {
    const el = document.createElement('textarea');
    el.value = property.place.formattedAddress;
    el.setAttribute('readonly', '');
    el.style.position = 'absolute';
    el.style.left = '-9999px';
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
    enqueueSnackbar("Copied to clipboard");
  };
  const classes = useStyles();
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h5">Location</Typography>
        <div className={classes.separator} />
        <Divider />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="p">{property.place.formattedAddress}</Typography>
        <IconButton size='small' className={classes.copyButton} onClick={handleCopyAddress}>
          <CopyIcon />
        </IconButton>
      </Grid>
      <ConditionalTooltip
        title={constants.helpText.adminOnlyEdit}
        visible={!admin && !disabled}
      >
        <Grid item xs={12} sm={3}>
          <TextField
            value={formik.values.place.addressComponents.streetNumber}
            onChange={formik.handleChange}
            disabled={!admin || disabled}
            error={
              formik.errors.place &&
              formik.errors.place.addressComponents &&
              formik.errors.place.addressComponents.streetNumber
            }
            fullWidth
            helperText={
              formik.errors.place &&
              formik.errors.place.addressComponents &&
              formik.errors.place.addressComponents.streetNumber
            }
            label="Street Number"
            name="place.addressComponents.streetNumber"
            onBlur={formik.handleBlur}
            variant="filled"
          />
        </Grid>
      </ConditionalTooltip>
      <ConditionalTooltip
        title={constants.helpText.adminOnlyEdit}
        visible={!admin && !disabled}
      >
        <Grid item xs={12} sm={6}>
          <TextField
            value={formik.values.place.addressComponents.streetName}
            onChange={formik.handleChange}
            disabled={!admin || disabled}
            error={
              formik.errors.place &&
              formik.errors.place.addressComponents &&
              formik.errors.place.addressComponents.streetName
            }
            fullWidth
            helperText={
              formik.errors.place &&
              formik.errors.place.addressComponents &&
              formik.errors.place.addressComponents.streetName
            }
            label="Street Name"
            name="place.addressComponents.streetName"
            onBlur={formik.handleBlur}
            variant="filled"
          />
        </Grid>
      </ConditionalTooltip>
      <ConditionalTooltip
        title={constants.helpText.adminOnlyEdit}
        visible={!admin && !disabled}
      >
        <Grid item xs={12} sm={3}>
          <TextField
            value={formik.values.place.addressComponents.subpremise}
            onChange={formik.handleChange}
            disabled={!admin || disabled}
            error={
              formik.errors.place &&
              formik.errors.place.addressComponents &&
              formik.errors.place.addressComponents.subpremise
            }
            fullWidth
            helperText={
              formik.errors.place &&
              formik.errors.place.addressComponents &&
              formik.errors.place.addressComponents.subpremise
            }
            InputProps={{
              startAdornment: (
                <HelpAdornment title={constants.helpText.propertySubpremise} />
              )
            }}
            label="Subpremise"
            name='place.addressComponents.subpremise'
            onBlur={formik.handleBlur}
            variant="filled"
          />
        </Grid>
      </ConditionalTooltip>
      <ConditionalTooltip
        title={constants.helpText.adminOnlyEdit}
        visible={!admin && !disabled}
      >
        <Grid item xs={12} sm={3}>
          <TextField
            value={formik.values.place.addressComponents.city}
            onChange={formik.handleChange}
            disabled={!admin || disabled}
            error={
              formik.errors.place &&
              formik.errors.place.addressComponents &&
              formik.errors.place.addressComponents.city
            }
            fullWidth
            helperText={
              formik.errors.place &&
              formik.errors.place.addressComponents &&
              formik.errors.place.addressComponents.city
            }
            label="City"
            name="place.addressComponents.city"
            onBlur={formik.handleBlur}
            variant="filled"
          />
        </Grid>
      </ConditionalTooltip>
      <ConditionalTooltip
        title={constants.helpText.adminOnlyEdit}
        visible={!admin && !disabled}
      >
        <Grid item xs={12} sm={3}>
          <TextField
            value={formik.values.place.addressComponents.state}
            onChange={formik.handleChange}
            disabled={!admin || disabled}
            error={
              formik.errors.place &&
              formik.errors.place.addressComponents &&
              formik.errors.place.addressComponents.state
            }
            fullWidth
            helperText={
              formik.errors.place &&
              formik.errors.place.addressComponents &&
              formik.errors.place.addressComponents.state
            }
            label="State"
            name="place.addressComponents.state"
            onBlur={formik.handleBlur}
            variant="filled"
          />
        </Grid>
      </ConditionalTooltip>
      <ConditionalTooltip
        title={constants.helpText.adminOnlyEdit}
        visible={!admin && !disabled}
      >
        <Grid item xs={12} sm={3}>
          <TextField
            value={formik.values.place.addressComponents.zipCode}
            onChange={formik.handleChange}
            disabled={!admin || disabled}
            error={
              formik.errors.place &&
              formik.errors.place.addressComponents &&
              formik.errors.place.addressComponents.zipCode
            }
            fullWidth
            helperText={
              formik.errors.place &&
              formik.errors.place.addressComponents &&
              formik.errors.place.addressComponents.zipCode
            }
            label="Zip"
            name="place.addressComponents.zipCode"
            onBlur={formik.handleBlur}
            variant="filled"
          />
        </Grid>
      </ConditionalTooltip>
      <ConditionalTooltip
        title={constants.helpText.adminOnlyEdit}
        visible={!admin && !disabled}
      >
        <Grid item xs={12} sm={3}>
          <TextField
            value={formik.values.place.addressComponents.country}
            onChange={formik.handleChange}
            disabled={!admin || disabled}
            error={
              formik.errors.place &&
              formik.errors.place.addressComponents &&
              formik.errors.place.addressComponents.country
            }
            fullWidth
            helperText={
              formik.errors.place &&
              formik.errors.place.addressComponents &&
              formik.errors.place.addressComponents.country
            }
            label="Country"
            name="place.addressComponents.country"
            onBlur={formik.handleBlur}
            variant="filled"
          />
        </Grid>
      </ConditionalTooltip>
      <Grid item xs={12}>
        <ListSubheader disableSticky>
          Address location (click to adjust)
        </ListSubheader>
        <div className={classes.map}>
          <GoogleMapReact
            bootstrapURLKeys={{
              key: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ""
            }}
            options={{
              panControl: true,
              mapTypeControl: true,
              scrollwheel: true,
              zoomControl: true,
              fullscreenControl: false
            }}
            defaultCenter={formik.values.place.coordinates}
            defaultZoom={18}
            onClick={handleMapClick}
          >
            <div
              className={classes.locationMarker}
              lat={formik.values.place.coordinates.lat}
              lng={formik.values.place.coordinates.lng}
            >
              <HomeIcon className={classes.locationMarkerIcon} />
            </div>
          </GoogleMapReact>
        </div>
      </Grid>
    </Grid>
  );
};

const useStyles = makeStyles(theme => ({
  separator: {
    height: 8
  },
  map: {
    height: 400,
    width: "100%",
    borderRadius: 8,
    overflow: "hidden"
  },
  locationMarker: {
    height: 32,
    width: 32,
    borderRadius: 16,
    backgroundColor: colors.KONTRA_BLUE,
    display: "flex",
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    transform: "translate(-50%,-50%)"
  },
  locationMarkerIcon: {
    color: "white",
    width: 24,
    height: 24
  },
  copyButton: {
    marginLeft: 16
  }
}));

export default Location;
